import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';
import ConfirmDialog from '../components/ConfirmDialog';

interface Tutor {
  id: string;
  name: string;
  photo: string;
  bio: string;
  expertise: string;
  experience: string;
  education: string;
  company: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type SortField = 'name' | 'company' | 'expertise';
type SortDirection = 'asc' | 'desc';

const client = generateClient<Schema>({ authMode: 'userPool' });

interface AdminTutorsProps {
  onEditTutor?: (tutorId: string) => void;
  refreshTrigger?: number; // When this changes, refresh the list
}

const AdminTutors: React.FC<AdminTutorsProps> = ({ onEditTutor, refreshTrigger }) => {
  const [tutors, setTutors] = useState<Tutor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; tutorId: string; tutorName: string }>({ isOpen: false, tutorId: '', tutorName: '' });
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchTutors();
  }, []);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchTutors();
    }
  }, [refreshTrigger]);

  const fetchTutors = async () => {
    try {
      setLoading(true);
      console.log('Fetching tutors from database...');
      
      // Fetch ALL tutors with pagination
      let allTutors: any[] = [];
      let nextToken: string | null | undefined = null;
      
      do {
        const response: any = await client.models.Tutor.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        allTutors = [...allTutors, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 AdminTutors: Fetched ${response.data?.length || 0} tutors (total: ${allTutors.length})`);
      } while (nextToken);
      
      console.log(`✅ AdminTutors: Total tutors fetched: ${allTutors.length}`);
      
      // Convert to our interface format
      const tutorsData = allTutors.map((tutor: any) => ({
        id: tutor.id,
        name: tutor.name || '',
        photo: tutor.photo || '',
        bio: tutor.bio || '',
        expertise: tutor.expertise || '',
        experience: tutor.experience || '',
        education: tutor.education || '',
        company: tutor.company || '',
        linkedin: tutor.linkedin || '',
        twitter: tutor.twitter || '',
        website: tutor.website || '',
        isActive: tutor.isActive !== undefined ? tutor.isActive : true,
        createdAt: tutor.createdAt || new Date().toISOString(),
        updatedAt: tutor.updatedAt || new Date().toISOString()
      }));
      
      setTutors(tutorsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutors:', err);
      setError(`Failed to fetch tutors: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setTutors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDeleteTutor = (tutorId: string) => {
    const tutor = tutors.find(t => t.id === tutorId);
    if (tutor) {
      setConfirmDialog({
        isOpen: true,
        tutorId,
        tutorName: tutor.name
      });
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      
      // SOFT DELETE: Set status to 'deleted' and add deletedAt timestamp
      // This preserves data immutability - never hard delete user data
      await client.models.Tutor.update({ 
        id: confirmDialog.tutorId,
        status: 'deleted',
        deletedAt: new Date().toISOString(),
        deletedBy: 'admin' // TODO: Get actual admin user email
      });
      
      setMessage({
        type: 'success',
        text: `Tutor "${confirmDialog.tutorName}" wurde erfolgreich gelöscht.`
      });
      
      // Refresh the list
      fetchTutors();
      
      // Close dialog
      setConfirmDialog({ isOpen: false, tutorId: '', tutorName: '' });
      
      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Error deleting tutor:', err);
      setMessage({
        type: 'error',
        text: `Fehler beim Löschen des Tutors: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const createTutorSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9äöüß]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleVisitTutor = (tutorName: string) => {
    const tutorSlug = createTutorSlug(tutorName);
    window.location.href = `/tutors/${tutorSlug}`;
  };

  const getSortedTutors = () => {
    return [...tutors].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  if (loading) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Loading tutors...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Error: {error}</h2>
          <button className="admin-btn primary" onClick={fetchTutors}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-card" style={{ padding: 0, paddingBottom: '60px' }}>
      {message && (
        <div style={{ padding: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}
      
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Tutor"
        message={`Are you sure you want to delete the tutor "${confirmDialog.tutorName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, tutorId: '', tutorName: '' })}
      />
      
      {getSortedTutors().length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
          <p>No tutors found. Create your first tutor.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', margin: 0, padding: 0 }}>
          <table className="admin-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  Tutor Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('company')} style={{ cursor: 'pointer' }}>
                  Company {sortField === 'company' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('expertise')} style={{ cursor: 'pointer' }}>
                  Expertise {sortField === 'expertise' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getSortedTutors().map((tutor) => (
                <tr key={tutor.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>
                      {tutor.name}
                    </div>
                    {tutor.education && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        {tutor.education}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#374151' }}>
                      {tutor.company || '-'}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {tutor.expertise ? (
                        tutor.expertise.split(',').slice(0, 2).map((exp, index) => (
                          <span key={index} style={{
                            display: 'inline-block',
                            background: '#F3F4F6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            margin: '1px',
                            fontSize: '11px'
                          }}>
                            {exp.trim()}
                          </span>
                        ))
                      ) : '-'}
                    </div>
                  </td>
                  <td>
                    <div className="actions">
                      {onEditTutor && (
                        <button
                          onClick={() => onEditTutor(tutor.id)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #8B5A2B',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#8B5A2B',
                            fontSize: '12px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteTutor(tutor.id)}
                        className="danger"
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #dc2626',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#dc2626',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginRight: '8px'
                        }}
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => handleVisitTutor(tutor.name)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #008F99',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Visit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminTutors;
